import { Button, Flex, Image, Link, Text } from '@chakra-ui/react'

import { H5 } from '@/components/ui/Headings'
import { logout } from '@/api/auth'
import { useLogoBgColor } from '@/hooks/use-logo-bg-color'

import LayoutToggle from './layout-toggle'

import type { LayoutMode } from '../types'

interface GalleryNavbarProps {
  brandName: string
  brandLogo: string | null
  brandColor: string | null
  designCount: number
  layoutMode: LayoutMode
  onChangeLayout: (mode: LayoutMode) => void
}

export default function GalleryNavbar({
  brandName,
  brandLogo,
  brandColor,
  designCount,
  layoutMode,
  onChangeLayout
}: GalleryNavbarProps) {
  const logoBgColor = useLogoBgColor(brandLogo)

  return (
    <Flex
      as="nav"
      position="sticky"
      top={0}
      zIndex={100}
      h="64px"
      px={{ base: '16px', md: '32px' }}
      alignItems="center"
      justifyContent="space-between"
      bg="rgba(10, 10, 15, 0.8)"
      backdropFilter="blur(20px) saturate(180%)"
      borderBottom="1px solid"
      borderColor="border.subtle"
      className="animate-fade-in"
    >
      <Flex alignItems="center" gap="12px">
        {brandLogo && (
          <Image
            src={brandLogo}
            alt={brandName}
            w="28px"
            h="28px"
            borderRadius="6px"
            objectFit="contain"
            bg={logoBgColor}
            p={logoBgColor ? '2px' : undefined}
          />
        )}
        <H5 color="text.primary" fontWeight={300}>
          {brandName}
        </H5>
        <Text textStyle="bodySuperSmall" color="text.tertiary">
          {designCount} designs
        </Text>
      </Flex>

      <Flex alignItems="center" gap="16px">
        <LayoutToggle
          layoutMode={layoutMode}
          onChangeLayout={onChangeLayout}
        />
        <Link
          href="mailto:michael@spacerunners.com"
          display="inline-flex"
          alignItems="center"
          h="36px"
          px="20px"
          textStyle="bodySmall"
          fontWeight={500}
          borderRadius="9999px"
          bg={brandColor ?? 'accent.500'}
          color="white"
          transition="all 0.3s ease"
          _hover={{
            opacity: 0.85,
            transform: 'translateY(-1px)',
            boxShadow: brandColor
              ? `0 0 30px ${brandColor}4D`
              : '0 0 30px rgba(0, 112, 243, 0.3)',
            textDecoration: 'none'
          }}
        >
          Chat with Us
        </Link>
        <Button
          h="36px"
          px="16px"
          textStyle="bodySmall"
          fontWeight={400}
          borderRadius="9999px"
          bg="transparent"
          color="text.tertiary"
          border="1px solid"
          borderColor="border.subtle"
          transition="all 0.3s ease"
          _hover={{
            color: 'text.primary',
            borderColor: 'border.strong'
          }}
          onClick={logout}
        >
          Sign Out
        </Button>
      </Flex>
    </Flex>
  )
}
